import { useDisclosure, useId } from '@mantine/hooks';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import React from 'react';
import { Collapse } from 'src/design/atoms/Collapse';
import { Group } from 'src/design/atoms/Group';
import { PaperStack } from 'src/design/atoms/PaperStack';
import { Title } from 'src/design/atoms/Title';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';

export interface ICollapsiblePaperSectionProps {
  title: string;
  /** Right side of the header, e.g. count badges or a summary. */
  trailing?: React.ReactNode;
  defaultOpened?: boolean;
  children?: React.ReactNode;
}

/** @registry PaperStack page section with a level-4 title that toggles its body open/closed and optional trailing header content; same surface as form PaperStack sections. */
export const CollapsiblePaperSection: React.FC<
  ICollapsiblePaperSectionProps
> = ({ title, trailing, defaultOpened = true, children }) => {
  const [opened, { toggle }] = useDisclosure(defaultOpened);
  const bodyId = useId();

  // A closed Collapse stays in the DOM at height 0, so a Stack gap would leave
  // a trailing gap; the header margin carries the spacing instead.
  return (
    <PaperStack stackProps={{ gap: 0 }}>
      <Group justify="space-between" mb={opened ? 'md' : 0}>
        <Title order={4}>
          {/* fz="inherit": Mantine's button reset pins font-size to md, which would override the heading size. */}
          <UnstyledButton
            onClick={toggle}
            aria-expanded={opened}
            aria-controls={bodyId}
            display="flex"
            fz="inherit"
            style={{ alignItems: 'center', gap: 'var(--mantine-spacing-xs)' }}
          >
            {title}
            {opened ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )}
          </UnstyledButton>
        </Title>
        {trailing}
      </Group>
      <Collapse id={bodyId} in={opened}>
        {children}
      </Collapse>
    </PaperStack>
  );
};
